import { Link, router } from "@inertiajs/react"
import {
  Bookmark,
  BookmarkCheck,
  Footprints,
  MapPin,
  Ruler,
} from "lucide-react"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { typedPost } from "@/lib/typed-router"
import type { SavePlayer } from "@/types/router-mutations"
import type {
  PlayerProfile,
  PlayerProfileSearchCardTrait,
} from "@/types/serializers"

const POSITION_LABELS: Record<string, string> = {
  portero: "Portero",
  defensa_central: "Defensa Central",
  lateral_derecho: "Lateral Derecho",
  lateral_izquierdo: "Lateral Izquierdo",
  mediocampista_central: "Mediocampista Central",
  mediocampista_ofensivo: "Mediocampista Ofensivo",
  mediocampista_defensivo: "Mediocampista Defensivo",
  extremo_derecho: "Extremo Derecho",
  extremo_izquierdo: "Extremo Izquierdo",
  delantero_centro: "Delantero Centro",
  segundo_delantero: "Segundo Delantero",
}

const FOOT_LABELS: Record<string, string> = {
  derecho: "Derecho",
  izquierdo: "Izquierdo",
  ambidiestro: "Ambidiestro",
}

const AVAILABILITY_CONFIG: Record<
  string,
  { label: string; className: string }
> = {
  disponible: {
    label: "Disponible",
    className: "bg-green-500/20 text-green-400 border-green-500/30",
  },
  no_disponible: {
    label: "No disponible",
    className: "bg-krown-grey-60/30 text-muted-foreground border-krown-grey-60",
  },
  escuchando_ofertas: {
    label: "Escuchando ofertas",
    className: "bg-orange-500/20 text-orange-400 border-orange-500/30",
  },
}

interface PlayerCardProps {
  player: PlayerProfile & PlayerProfileSearchCardTrait
  userRole?: string | null
  isSaved?: boolean
  savedPlayerId?: number | null
}

function calculateAge(dateOfBirth: string | null): number | null {
  if (!dateOfBirth) return null
  const today = new Date()
  const birthDate = new Date(dateOfBirth)
  let age = today.getFullYear() - birthDate.getFullYear()
  const monthDiff = today.getMonth() - birthDate.getMonth()
  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--
  }
  return age
}

export default function PlayerCard({
  player,
  userRole,
  isSaved,
  savedPlayerId,
}: PlayerCardProps) {
  const age = calculateAge(player.date_of_birth)
  const positionLabel = POSITION_LABELS[player.position] ?? player.position
  const footLabel = FOOT_LABELS[player.dominant_foot] ?? player.dominant_foot
  const availability =
    AVAILABILITY_CONFIG[player.availability] ?? AVAILABILITY_CONFIG.disponible

  const canBookmark = userRole === "scout" || userRole === "club"

  function handleBookmarkClick(e: React.MouseEvent) {
    e.preventDefault()
    e.stopPropagation()

    if (isSaved && savedPlayerId) {
      router.delete(`/guardados/${savedPlayerId}`, { preserveState: true })
    } else {
      typedPost<SavePlayer>(
        "/guardados",
        { saved_player: { player_profile_id: player.id } },
        { preserveState: true },
      )
    }
  }

  return (
    <Link href={`/jugadores/${player.slug}`} className="block">
      <div className="border-border bg-card hover:border-krown-300 rounded-lg border p-4 transition-colors">
        <div className="flex items-start gap-3">
          <Avatar className="h-12 w-12 shrink-0">
            <AvatarImage
              src={player.user.avatar_url ?? undefined}
              alt={player.user.full_name}
            />
            <AvatarFallback className="bg-krown-200 text-sm font-bold text-white">
              {player.user.first_name[0]}
              {player.user.last_name[0]}
            </AvatarFallback>
          </Avatar>

          <div className="min-w-0 flex-1">
            <h3 className="truncate font-bold text-white">
              {player.user.full_name}
            </h3>
            {age != null && (
              <p className="text-muted-foreground text-sm">{age} años</p>
            )}
          </div>

          {canBookmark && (
            <button
              type="button"
              onClick={handleBookmarkClick}
              aria-label={isSaved ? "Quitar de guardados" : "Guardar jugador"}
              className="text-muted-foreground hover:text-krown-crown shrink-0 p-1 transition-colors"
            >
              {isSaved ? (
                <BookmarkCheck className="text-krown-crown h-5 w-5" />
              ) : (
                <Bookmark className="h-5 w-5" />
              )}
            </button>
          )}
        </div>

        <div className="mt-3 flex flex-wrap gap-1.5">
          <span className="bg-krown-200/20 text-krown-100 border-krown-200/30 inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold tracking-wider uppercase">
            {positionLabel}
          </span>
          <span
            className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${availability.className}`}
          >
            {availability.label}
          </span>
        </div>

        <div className="text-muted-foreground mt-3 space-y-1 text-sm">
          <div className="flex items-center gap-1.5">
            <MapPin className="text-krown-200 h-3.5 w-3.5 shrink-0" />
            <span className="truncate">
              {[player.city, player.country].filter(Boolean).join(", ")}
            </span>
          </div>
          <div className="flex items-center gap-3">
            {player.height_cm && (
              <span className="flex items-center gap-1">
                <Ruler className="text-krown-200 h-3.5 w-3.5" />
                {player.height_cm} cm
              </span>
            )}
            <span className="flex items-center gap-1">
              <Footprints className="text-krown-200 h-3.5 w-3.5" />
              {footLabel}
            </span>
          </div>
        </div>
      </div>
    </Link>
  )
}
